/**
 * @author alteredq / http://alteredqualia.com/
 * @author mr.doob / http://mrdoob.com/
 */

import * as THREE from "three";
import Particle from "./particle.js";
import { ParametricGeometry } from "three/addons/geometries/ParametricGeometry.js";

export default class Cloth {
  MASS = 0.25;
  GRAVITY = 981 * 1.4;

  TIMESTEP = 12 / 1000;
  TIMESTEP_SQ = this.TIMESTEP * this.TIMESTEP;

  X_SEGS = 16;
  Y_SEGS = 16;
  REST_DISTANCE = 25;

  constructor() {
    this.particles = [];
    this.constraints = [];

    this.index = 0;

    this.width = 16;
    this.height = 16;

    this.clothGeometry = new ParametricGeometry(
      this._clothFunction,
      this.width,
      this.height,
    );

    this.pins = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
    this.pinsFormation = [
      [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
    ];

    this.lastTime = null;
    this.tmpForce = new THREE.Vector3();
    this.gravity = new THREE.Vector3(0, -this.GRAVITY, 0).multiplyScalar(
      this.MASS,
    );
    this.diff = new THREE.Vector3();
  }

  render() {
    this._generateParticles();

    const loader = new THREE.TextureLoader();
    const clothTexture = loader.load("./assets/img/hero/utl.webp");
    clothTexture.anisotropy = 8;

    const clothMaterial = new THREE.MeshLambertMaterial({
      map: clothTexture,
      side: THREE.BackSide,
    });

    const clothObject = new THREE.Mesh(this.clothGeometry, clothMaterial);
    clothObject.position.set(0, 50, 0);
    clothObject.castShadow = true;

    clothObject.customDepthMaterial = new THREE.MeshDepthMaterial({
      depthPacking: THREE.RGBADepthPacking,
      map: clothTexture,
      alphaTest: 1,
      depthTest: false,
      depthWrite: false,
    });

    return clothObject;
  }

  animate(time, windForce) {
    if (!this.lastTime) {
      this.lastTime = time;
      return;
    }

    let indx;
    const normal = new THREE.Vector3();
    const indices = this.clothGeometry.index;
    const normals = this.clothGeometry.attributes.normal;

    for (let i = 0, il = indices.count; i < il; i += 3) {
      for (let j = 0; j < 3; j++) {
        indx = indices.getX(i + j);
        normal.fromBufferAttribute(normals, indx);
        this.tmpForce
          .copy(normal)
          .normalize()
          .multiplyScalar(normal.dot(windForce));
        this.particles[indx].addForce(this.tmpForce);
      }
    }

    for (
      let particles = this.particles, i = 0, il = this.particles.length;
      i < il;
      i++
    ) {
      const particle = particles[i];
      particle.addForce(this.gravity);

      particle.integrate(this.TIMESTEP_SQ);
    }

    for (let i = 0; i < this.constraints.length; i++) {
      const constraint = this.constraints[i];
      this._satisfyConstraints(constraint[0], constraint[1], constraint[2]);
    }

    // Floor Constraints
    for (let i = 0, il = this.particles.length; i < il; i++) {
      const pos = this.particles[i].position;

      if (pos.y < -250) pos.y = -250;
    }

    // Pin Constraints
    for (let i = 0, il = this.pins.length; i < il; i++) {
      const xy = this.pins[i];
      const p = this.particles[xy];
      p.position.copy(p.original);
      p.previous.copy(p.original);
    }

    const particles = this.particles;

    for (let i = 0, il = particles.length; i < il; i++) {
      const v = particles[i].position;

      this.clothGeometry.attributes.position.setXYZ(i, v.x, v.y, v.z);
    }

    this.clothGeometry.attributes.position.needsUpdate = true;
    this.clothGeometry.computeVertexNormals();
  }

  _generateParticles() {
    for (let y = 0; y <= this.height; y++) {
      for (let x = 0; x <= this.width; x++) {
        this.particles.push(
          new Particle(
            x / this.width,
            y / this.height,
            this.MASS,
            this.REST_DISTANCE,
            this._clothFunction,
          ),
        );
      }
    }

    // Structural
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        this.constraints.push([
          this.particles[this._calculateIndex(x, y)],
          this.particles[this._calculateIndex(x, y + 1)],
          this.REST_DISTANCE,
        ]);

        this.constraints.push([
          this.particles[this._calculateIndex(x, y)],
          this.particles[this._calculateIndex(x + 1, y)],
          this.REST_DISTANCE,
        ]);
      }
    }

    for (let x = this.width, y = 0; y < this.height; y++) {
      this.constraints.push([
        this.particles[this._calculateIndex(x, y)],
        this.particles[this._calculateIndex(x, y + 1)],
        this.REST_DISTANCE,
      ]);
    }

    for (let y = this.height, x = 0; x < this.width; x++) {
      this.constraints.push([
        this.particles[this._calculateIndex(x, y)],
        this.particles[this._calculateIndex(x + 1, y)],
        this.REST_DISTANCE,
      ]);
    }
  }

  _satisfyConstraints(p1, p2, distance) {
    this.diff.subVectors(p2.position, p1.position);
    const currentDist = this.diff.length();

    if (currentDist === 0) return;

    const correction = this.diff.multiplyScalar(1 - distance / currentDist);
    const correctionHalf = correction.multiplyScalar(0.5);

    p1.position.add(correctionHalf);
    p2.position.sub(correctionHalf);
  }

  _calculateIndex(x, y) {
    return x + y * (this.width + 1);
  }

  _clothFunction = this._plane(
    this.REST_DISTANCE * this.X_SEGS,
    this.REST_DISTANCE * this.Y_SEGS,
  );

  _plane(width, height) {
    return function (u, v, target) {
      const x = (u - 0.5) * width;
      const y = (v + 0.5) * height;
      const z = 0;

      target.set(x, y, z);
    };
  }
}
